import React, { forwardRef, memo, useCallback, useImperativeHandle } from 'react';
import { useAppDispatch, useAppSelector, useTranslate, useWidgetsTheme as theme } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { Product } from '@src/core/buyerApiClient';
import ProductAdderHeader from '@src/domains/products/features/productAdder/components/ProductAdderHeader';
import AdderProductList, {
  ProductListFooterInfo,
} from '@src/domains/products/features/multiProductBooking/AdderProductList';
import AdderBody from '@src/domains/products/features/multiProductBooking/AdderBody';
import AdderCancelModal from '@src/domains/products/features/multiProductBooking/AdderCancelModal';
import CompletedSummary from '@src/domains/products/features/multiProductBooking/CompletedSummary';
import { useMultiProductSelection } from '@src/domains/products/features/multiProductBooking/useMultiProductSelection';
import Alert, { AlertTitle } from '@src/components/reusable/Alert';
import { IconInfoCircle } from '@tabler/icons-react';
import { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import {
  clearHighlightedProductAdder,
  currentProductAdderStatus,
  isClosingAdderModalSelector,
  productSelectionStateSelector,
  toggleIsClosingAdderModal,
} from '@src/stores/product';
import { closeMultiProductBookingModal, multiProductBookingSelector } from '@src/stores/multiProductBooking';
import { appInfoSelector } from '@src/stores/app';
import useBreakpoint from '@src/hooks/useBreakpoint';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { formatCents } from '@src/common';
import { ErrorWrapper, Grid, ProductAdderArea } from './styles';

export interface MultiProductBookingModalRef {
  triggerClose: () => void;
}

interface MultiProductBookingModalProps {
  dataCy: string;
  title: string | undefined;
  products: Product[];
  onSelect: (productId: string) => void;
  showListTotal?: boolean;
  completedDescription: string | ((count: number) => string);
  totalCents: number;
  onAddToCart: () => void;
  onConfirmAbandon: () => void;
  cancelTitle: string;
  cancelDescription: string;
  cancelConfirmLabel: string;
  cancelCancelLabel: string;
  alwaysConfirmClose: boolean;
  showProductListWhenCompleted?: boolean;
  disableAllProducts?: boolean;
  error?: string;
}

const MultiProductBookingModal = forwardRef<MultiProductBookingModalRef, MultiProductBookingModalProps>(
  (
    {
      dataCy,
      title,
      products,
      onSelect,
      showListTotal,
      completedDescription,
      totalCents,
      onAddToCart,
      onConfirmAbandon,
      cancelTitle,
      cancelDescription,
      cancelConfirmLabel,
      cancelCancelLabel,
      alwaysConfirmClose,
      showProductListWhenCompleted,
      disableAllProducts,
      error,
    },
    ref,
  ) => {
    const widgetId = useWidgetId();
    const dispatch = useAppDispatch();
    const { i18n } = useTranslate();
    const actionColor = theme('actionColor');
    const { isDesktop, isMobile } = useBreakpoint();
    const appInfo = useAppSelector(appInfoSelector);

    const isClosing = useAppSelector(isClosingAdderModalSelector(widgetId));
    const currentAdderStatus = useAppSelector(currentProductAdderStatus(widgetId));
    const booking = useAppSelector(multiProductBookingSelector);
    const productSelectionState = useAppSelector(productSelectionStateSelector(widgetId));
    const selectedProductId = useSelectedProductId();

    const {
      selectedProduct,
      loadingProduct,
      countFilledProducts,
      isProductFilled,
      getSelectedDate,
      getSelectedTime,
      getSelectedTariffs,
      shouldProductBeDisabled,
    } = useMultiProductSelection(products);

    const isCompleted = Boolean(booking?.isCompleted && currentAdderStatus !== 'finished_request');

    const resolvedDescription =
      typeof completedDescription === 'function' ? completedDescription(countFilledProducts) : completedDescription;

    const handleCloseAttempt = useCallback(
      (callback: () => void) => {
        if (alwaysConfirmClose || Object.keys(productSelectionState).length > 0 || selectedProductId) {
          dispatch(toggleIsClosingAdderModal(widgetId, true));
        } else {
          dispatch(closeMultiProductBookingModal());
          callback();
        }
      },
      [alwaysConfirmClose, productSelectionState, selectedProductId, dispatch, widgetId],
    );

    const handleCancelClose = useCallback(() => {
      dispatch(toggleIsClosingAdderModal(widgetId, false));
    }, [dispatch, widgetId]);

    const handleConfirmAbandon = useCallback(() => {
      dispatch(toggleIsClosingAdderModal(widgetId, false));
      onConfirmAbandon();
    }, [dispatch, widgetId, onConfirmAbandon]);

    useImperativeHandle(
      ref,
      () => ({
        triggerClose: () => {
          handleCloseAttempt(() => dispatch(clearHighlightedProductAdder()));
        },
      }),
      [handleCloseAttempt, dispatch],
    );

    const shouldRenderProductList =
      isDesktop || !selectedProductId || Boolean(error) || (isCompleted && Boolean(showProductListWhenCompleted));
    const shouldRenderBody = !error && (isDesktop || Boolean(selectedProductId) || (isMobile && isCompleted));

    const footer = showListTotal ? (
      <ProductListFooterInfo>
        <span>{i18n('product_package_adder_total')}: </span>
        <strong>{formatCents(totalCents)}</strong>
      </ProductListFooterInfo>
    ) : undefined;

    const errorNode = error ? (
      <ErrorWrapper>
        <Alert severity='error' variant='ghost' icon={<IconInfoCircle size={24} />}>
          <AlertTitle>{i18n(error as DefaultMessagesKeys)}</AlertTitle>
        </Alert>
      </ErrorWrapper>
    ) : null;

    return (
      <>
        <ProductAdderArea data-cy={dataCy}>
          <ProductAdderHeader title={title} onClose={handleCloseAttempt} />
          <Grid>
            {shouldRenderProductList ? (
              <>
                {!isDesktop ? errorNode : null}
                <AdderProductList
                  title={i18n('product_package_adder_product_list_title')}
                  products={products}
                  selectedProductId={selectedProductId}
                  countFilled={countFilledProducts}
                  isAllFilled={Boolean(booking?.isCompleted)}
                  isProductFilled={isProductFilled}
                  getSelectedDate={getSelectedDate}
                  getSelectedTime={getSelectedTime}
                  getSelectedTariffs={getSelectedTariffs}
                  shouldBeDisabled={(id) => Boolean(disableAllProducts) || shouldProductBeDisabled(id)}
                  onSelect={onSelect}
                  appId={appInfo?.id}
                  footer={footer}
                />
              </>
            ) : null}

            {isCompleted ? (
              <CompletedSummary description={resolvedDescription} totalCents={totalCents} onAddToCart={onAddToCart} />
            ) : shouldRenderBody ? (
              <AdderBody selectedProduct={selectedProduct} loadingProduct={loadingProduct ?? false} />
            ) : null}

            {isDesktop ? errorNode : null}
          </Grid>
        </ProductAdderArea>

        {isClosing ? (
          <AdderCancelModal
            isOpen
            actionColor={actionColor}
            title={cancelTitle}
            description={cancelDescription}
            confirmLabel={cancelConfirmLabel}
            cancelLabel={cancelCancelLabel}
            onClose={handleCancelClose}
            onConfirm={handleConfirmAbandon}
          />
        ) : null}
      </>
    );
  },
);

MultiProductBookingModal.displayName = 'MultiProductBookingModal';

export default memo(MultiProductBookingModal);
